import { createFilter, GroupBase, StylesConfig } from 'react-select';
import { Option, SelectSize, SelectStyleProps } from './types';

export const getFilterOption = <T extends Option<any> | null>() =>
  createFilter<T>({
    stringify: ({ data }) => {
      if (!data) {
        return '';
      }
      return data.searchString ?? (typeof data.label === 'string' ? data.label : data?.value ?? '');
    },
  });

const SELECT_SIZE_MAP: { [key in SelectSize]: number } = {
  xl: 48,
  lg: 40,
  md: 32,
  sm: 28,
  xs: 24,
};
const getColor = ({
  isSelected,
  isDisabled,
  menuIsOpen,
  isTransparent,
}: {
  isSelected?: boolean;
  isDisabled: boolean;
  menuIsOpen?: boolean;
  isTransparent?: boolean;
}) => {
  if (menuIsOpen && isTransparent) {
    return 'var(--theme-TextDark)';
  }
  if (isSelected) {
    return 'white';
  }
  return isDisabled ? 'var(--theme-TextPlaceholder)' : 'var(--theme-TextMedium)';
};

const getBorderColor = ({
  isDisabled,
  isFocused,
  isTransparent = true,
  isInvalid = false
}: {
  isDisabled: boolean;
  isTransparent?: boolean;
  isFocused: boolean;
  isInvalid?: boolean;
}) => {
  if (isInvalid) {
    return 'var(--theme-DangerMedium)';
  }
  if (isFocused) {
    return 'var(--theme-NeutralsDark)';
  }
  if (isTransparent) {
    return 'transparent';
  }
  return isDisabled ? 'var(--theme-BgDisabled)' : 'var(--theme-NeutralsDark)';
};

export const getIconColor = ({
  isSelected,
  isDisabled,
  menuIsOpen,
  isTransparent,
}: {
  isSelected?: boolean;
  isDisabled: boolean;
  menuIsOpen?: boolean;
  isTransparent?: boolean;
}) => {
  if (menuIsOpen && isTransparent) {
    return 'var(--theme-TextDark)';
  }
  if (isSelected) {
    return 'white';
  }
  return isDisabled ? 'var(--theme-BgDisabled)' : 'var(--theme-IconSecondary)';
};

export const getBackgroundColor = ({ isSelected, isFocused }: { isSelected?: boolean; isFocused: boolean }) => {
  if (isSelected) {
    return 'var(--theme-AccentExtradark)';
  }

  return isFocused ? 'var(--theme-AccentExtralight)' : 'white';
};

export const getStyles = <T = Option | null,>({
  selectedOptions,
  isMulti,
  isTransparent = false,
  isFlexibleSize = false,
  showSelectAll = true,
  isMenuPortalTargetBody = false,
  size = 'md',
  hasOptionDivider = false,
  minWidth = 200,
  backgroundColor = 'transparent',
  isInvalid,
}: {
  selectedOptions?: T[];
  isMulti: boolean;
  showSelectAll?: boolean;
} & Pick<
  SelectStyleProps,
  | 'isFlexibleSize'
  | 'hasOptionDivider'
  | 'minWidth'
  | 'isTransparent'
  | 'size'
  | 'isMenuPortalTargetBody'
  | 'backgroundColor'
  | 'isInvalid'
>): StylesConfig<T, boolean, GroupBase<T>> => {
  return {
    container: (provided) => ({
      ...provided,
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: isFlexibleSize ? 'fit-content' : SELECT_SIZE_MAP[size],
      height: SELECT_SIZE_MAP[size],
      minWidth: isFlexibleSize ? 'inherit' : `${minWidth}px`,
      boxShadow: 'none',
      fontWeight: 'normal',
      '&:hover': {
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: isTransparent ? 'transparent' : 'var(--theme-NeutralsLight)',
        ...(isTransparent
          ? {
              color: 'var(--theme-TextDark)',
              i: {
                color: 'var(--theme-TextDark)',
              },
            }
          : {}),
      },
      color: getColor({ ...state, isTransparent }),
      backgroundColor: state.menuIsOpen && !isTransparent ? 'var(--theme-NeutralsLight)' : backgroundColor,
      borderColor: getBorderColor({ ...state, isTransparent, isInvalid }),
      i: {
        color: getIconColor({ ...state, isTransparent }),
      },
    }),
    menu: (provided) => ({
      ...provided,
      margin: 0,
      minWidth: isMulti ? 'fit-content' : isFlexibleSize ? 'max-content' : `${minWidth}px`,
      whiteSpace: 'normal',
      border: 'solid 1px var(--theme-NeutralsLight)',
      borderTop: 0,
      boxShadow: '0px 4px 8px 0 rgba(0, 0, 0, 0.1)',
      paddingBottom: 4,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
    }),
    menuPortal: (provided) => ({ ...provided, ...(isMenuPortalTargetBody ? { zIndex: 1050 } : {}) }),
    placeholder: (provided) => ({
      ...provided,
      color: 'inherit',
      margin: 0,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 0,
      color: 'var(--theme-IconSecondary)',
      '&:hover': {
        color: 'var(--theme-TextDark)',
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '0px',
      marginRight: '10px',
      fontSize: '1rem',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
      paddingLeft: '8px',
    }),
    input: (provided) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
    option: (provided, state) => {
      const isSelected =
        state.isSelected ||
        (selectedOptions &&
          selectedOptions.some((op) =>
            op !== null &&
            op !== undefined &&
            typeof op === 'object' &&
            state.data !== null &&
            typeof state.data === 'object' &&
            'value' in state.data &&
            'value' in op
              ? op.value === state.data.value
              : false
          ));
      const isDisabled = state.isDisabled;
      const isFocused = state.isFocused;

      return {
        ...provided,
        backgroundColor: getBackgroundColor({ isSelected, isFocused }),
        minHeight: SELECT_SIZE_MAP[size],
        display: 'flex',
        alignItems: 'center',
        borderColor: 'var(--theme-NeutralsLight)',
        borderStyle: 'solid',
        padding: '0 12px',
        borderWidth: hasOptionDivider ? '1px 0' : '0',
        color: getColor({ isSelected, isDisabled }),
        i: {
          color: getIconColor({ isSelected, isDisabled }),
        },
        img: {
          opacity: state.isDisabled ? 0.5 : 1,
        },
        '&:hover': {
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        },
        '&:first-of-type': {
          borderRadius: '4px 4px 0 0',
        },
        ...(isMulti && showSelectAll
          ? {
              '&:first-of-type': {
                borderBottom: '1px solid var(--theme-NeutralsLight)',
              },
            }
          : {}), // used to distinguish between select all and other options
      };
    },
    singleValue: (base) => ({
      ...base,
      margin: 0,
      color: 'inherit',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--theme-AccentExtradark)',
      color: 'white',
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: 'white',
      whiteSpace: 'normal',
      fontSize: '0.6875rem !important',
      i: {
        color: 'white',
        fontSize: '11px',
      },
      paddingTop: 0,
      paddingBottom: 0,
      padding: '0 2px 0 8px !important',
    }),
    multiValueRemove: (base) => ({
      ...base,
      i: {
        color: 'white',
        fontSize: '6px !important',
      },
      paddingLeft: 8,
      paddingRight: 8,
      borderRadius: 4,
    }),
  };
};
